import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import {
  FiMonitor, FiTablet, FiSmartphone, FiSettings, FiPlus, FiSave, FiCloud, FiMaximize, FiCheck, FiAlertCircle, FiTrash2, FiImage, FiUploadCloud, FiX, FiMenu, FiShoppingCart, FiEye, FiZap, FiLoader, FiMessageCircle
} from 'react-icons/fi';
import LogoPopup from './LogoPopup';
import MenuPopup from './MenuPopup';
import ButtonPopup from './ButtonPopup';
import ContainerPopup from './ContainerPopup';
import ContextMenu from './ContextMenu';
import LibraryPopup from './LibraryPopup';
import SocialLinksPopup from './SocialLinksPopup';
import EditableText from './EditableText';
import LivePreviewOverlay from './LivePreviewOverlay';
import ProductsWidgetPopup from './ProductsWidgetPopup';
import ProductsWidget from './widgets/ProductsWidget';
import ContactFormWidget from './widgets/ContactFormWidget';
import ContactFormPopup from './ContactFormPopup';
import ReviewsWidgetPopup from './ReviewsWidgetPopup';
import ReviewsWidget from './widgets/ReviewsWidget';
import ArtistWidget from './widgets/ArtistWidget';
import ArtistWidgetPopup from './ArtistWidgetPopup';
import ImageSettingsPopup from './ImageSettingsPopup';
import ImageWidgetPopup from './ImageWidgetPopup';
import GlobalFooter, { DEFAULT_FOOTER } from './GlobalFooter';
import supabase, { withRetry, subscribeToTable } from '../../supabase/supabase';
import { useCart } from '../../context/CartContext';

import { moveElement, buildNewElement } from '../../utils/canvasHelpers';
import { DropZone } from './canvas/DropZone';
import { SaveBadge } from './canvas/SaveBadge';
import { WidgetWrapper } from './canvas/WidgetWrapper';
import { ResizeDragOverlay } from './canvas/ResizeDragOverlay';
import { TextResizeHandles, ImageResizeHandles } from './canvas/ResizeHandles';

const Canvas = forwardRef(({ pageId, activeWebsiteId, savedPageContent, menuStyles = {}, onMenuStylesChange, socialLinks = {}, onSocialLinksChange, onHistoryChange, showPreview, onClosePreview, onOpenPreview, activePage, externalMenuItems, onMenuItemsChange, externalLogoData, onLogoDataChange, externalFooterData, onFooterDataChange, onPageStateChange, onSaveComplete, isReadOnly, pages, activeWebsiteName, onNavigateToPage }, ref) => {
  const { cartCount, setIsCartOpen } = useCart();
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [showLogoPopup, setShowLogoPopup] = useState(false);
  const [showMenuPopup, setShowMenuPopup] = useState(false);
  const [showButtonPopup, setShowButtonPopup] = useState(false);
  const [showContainerPopup, setShowContainerPopup] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [showImageSettingsPopup, setShowImageSettingsPopup] = useState(false);
  const [showSocialLinksPopup, setShowSocialLinksPopup] = useState(false);
  const [showProductsPopup, setShowProductsPopup] = useState(false);
  const [showContactFormPopup, setShowContactFormPopup] = useState(false);
  const [showReviewsPopup, setShowReviewsPopup] = useState(false);
  const [showArtistPopup, setShowArtistPopup] = useState(false);
  const [editingProductsId, setEditingProductsId] = useState(null);
  const [editingContactFormId, setEditingContactFormId] = useState(null);
  const [editingReviewsId, setEditingReviewsId] = useState(null);
  const [editingArtistId, setEditingArtistId] = useState(null);
  const [editingImageId, setEditingImageId] = useState(null);
  const [deviceView, setDeviceView] = useState('desktop');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [pendingImageWidgetId, setPendingImageWidgetId] = useState(null);
  const [libraryCallback, setLibraryCallback] = useState(null);
  const [editingButtonId, setEditingButtonId] = useState(null);
  const [editingContainerId, setEditingContainerId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [whatsappWidget, setWhatsappWidget] = useState({ enabled: false, number: '' });

  const [widgetResizeDrag, setWidgetResizeDrag] = useState(null);
  const [liveResizeVal, setLiveResizeVal] = useState(0);
  const [marginDrag, setMarginDrag] = useState(null);
  const [paddingDrag, setPaddingDrag] = useState(null);
  const [headerDrag, setHeaderDrag] = useState(null);
  const [canvasDrag, setCanvasDrag] = useState(null);

  const [headerPadding, setHeaderPadding] = useState({ top: 32, bottom: 32 });
  const [canvasPadding, setCanvasPadding] = useState({ top: 64, bottom: 64 });
  const [logoData, setLogoData] = useState(externalLogoData || { type: 'text', content: 'BRAND LOGO', url: '' });
  const [menuItems, setMenuItems] = useState(externalMenuItems || [{ id: 1, label: 'Home', link: '/' }]);
  const [currentSocialLinks, setCurrentSocialLinks] = useState(socialLinks || {});
  const [footerData, setFooterData] = useState(externalFooterData || DEFAULT_FOOTER);
  const [droppedElements, setDroppedElements] = useState([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [draggingOverCol, setDraggingOverCol] = useState(null);
  const canvasRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const isUndoRedo = useRef(false);

  useEffect(() => { if (externalMenuItems) setMenuItems(externalMenuItems); }, [externalMenuItems]);
  useEffect(() => { if (externalLogoData) setLogoData(externalLogoData); }, [externalLogoData]);
  useEffect(() => { if (externalFooterData) setFooterData(externalFooterData); }, [externalFooterData]);

  const handleMenuItemsUpdate = (items) => { setMenuItems(items); if (onMenuItemsChange) onMenuItemsChange(items); };
  const handleLogoUpdate = (data) => { setLogoData(data); if (onLogoDataChange) onLogoDataChange(data); };
  const handleFooterUpdate = (data) => { setFooterData(data); if (onFooterDataChange) onFooterDataChange(data); };

  useEffect(() => { if (deviceView === 'desktop') setIsMobileMenuOpen(false); }, [deviceView]);

  useEffect(() => {
    if (!activeWebsiteId) return;
    let isMounted = true;
    let unsubscribe = null;

    const loadWhatsappWidget = async () => {
      try {
        const result = await withRetry(() => supabase.from('websites_1772750000000').select('layout_data').eq('id', activeWebsiteId).single(), 2, 1000, 8000);
        if (result.error && result.error.code !== 'PGRST116') throw result.error;
        if (isMounted && result.data?.layout_data?.whatsappWidget) setWhatsappWidget(result.data.layout_data.whatsappWidget);
      } catch (err) {
        console.error('Failed to load WhatsApp widget settings:', err);
      }
    };
    
    loadWhatsappWidget();

    try {
      if (typeof subscribeToTable === 'function') {
        unsubscribe = subscribeToTable('websites_1772750000000', { filter: `id=eq.${activeWebsiteId}` }, (payload) => {
          if (isMounted && payload.new?.layout_data?.whatsappWidget) {
            setWhatsappWidget(payload.new.layout_data.whatsappWidget);
          }
        });
      }
    } catch (err) {
      console.warn('Realtime subscription setup failed:', err);
    }

    return () => {
      isMounted = false;
      if (unsubscribe) { try { unsubscribe(); } catch (e) { console.warn('Realtime cleanup error:', e); } }
    };
  }, [activeWebsiteId]);

  const handleNavClick = useCallback((e, link, linkType) => {
    if (!link) { e.preventDefault(); return; }
    const isAnchor = linkType === 'anchor' || link.startsWith('#');
    if (isAnchor) {
      e.preventDefault();
      const anchorId = link.startsWith('#') ? link.substring(1) : link;
      const target = document.getElementById(anchorId);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false); return;
    }
    if (linkType === 'page' || (!linkType && link.startsWith('/'))) {
      e.preventDefault();
      if (pages && pages.length > 0) {
        const targetPage = pages.find(p => p.slug === link || `/${p.slug}` === link || p.slug === `/${link}`);
        if (targetPage && onNavigateToPage) { onNavigateToPage(link); setIsMobileMenuOpen(false); return; }
      }
      if (onNavigateToPage) onNavigateToPage(link);
      setIsMobileMenuOpen(false); return;
    }
    e.preventDefault();
    window.open(link, '_blank');
  }, [pages, onNavigateToPage]);

  const [history, setHistory] = useState({ stack: [], index: -1 });
  const currentState = useMemo(() => ({ droppedElements, headerPadding, canvasPadding, logoData, menuItems, currentSocialLinks, menuStyles, footerData }), [droppedElements, headerPadding, canvasPadding, logoData, menuItems, currentSocialLinks, menuStyles, footerData]);

  useEffect(() => {
    if (onHistoryChange) onHistoryChange({ canUndo: history.index > 0, canRedo: history.index < history.stack.length - 1 });
  }, [history.index, history.stack.length, onHistoryChange]);

  const applyState = useCallback((state) => {
    if (!state) return;
    setDroppedElements(state.droppedElements || []);
    setHeaderPadding({ top: state.headerPadding?.top ?? 32, bottom: state.headerPadding?.bottom ?? 32 });
    setCanvasPadding({ top: state.canvasPadding?.top ?? 64, bottom: state.canvasPadding?.bottom ?? 64 });
    setLogoData(state.logoData || { type: 'text', content: 'BRAND LOGO', url: '' });
    setMenuItems(state.menuItems || []);
    setCurrentSocialLinks(state.currentSocialLinks || {});
    setFooterData(state.footerData || DEFAULT_FOOTER);
    if (onSocialLinksChange) onSocialLinksChange(state.currentSocialLinks || {});
    if (onMenuStylesChange) onMenuStylesChange(state.menuStyles || {});
  }, [onSocialLinksChange, onMenuStylesChange]);

  const undo = useCallback(() => {
    setHistory(prev => {
      if (prev.index > 0) { const i = prev.index - 1; isUndoRedo.current = true; applyState(prev.stack[i]); return { ...prev, index: i }; }
      return prev;
    });
  }, [applyState]);

  const redo = useCallback(() => {
    setHistory(prev => {
      if (prev.index < prev.stack.length - 1) { const i = prev.index + 1; isUndoRedo.current = true; applyState(prev.stack[i]); return { ...prev, index: i }; }
      return prev;
    });
  }, [applyState]);

  const getState = useCallback(() => ({ droppedElements, headerPadding, canvasPadding, logoData, menuItems, currentSocialLinks, menuStyles, footerData }), [droppedElements, headerPadding, canvasPadding, logoData, menuItems, currentSocialLinks, menuStyles, footerData]);

  const triggerSave = useCallback(async () => {
    const state = getState();
    if (onPageStateChange) onPageStateChange(pageId, state);
    if (onSaveComplete) onSaveComplete();
  }, [pageId, getState, onPageStateChange, onSaveComplete]);

  useImperativeHandle(ref, () => ({ undo, redo, getState, triggerSave }), [undo, redo, getState, triggerSave]);

  useEffect(() => {
    setIsLoaded(false);
    if (savedPageContent && Object.keys(savedPageContent).length > 0) {
      setDroppedElements(savedPageContent.droppedElements || []);
      setHeaderPadding({ top: savedPageContent.headerPadding?.top ?? 32, bottom: savedPageContent.headerPadding?.bottom ?? 32 });
      setCanvasPadding({ top: savedPageContent.canvasPadding?.top ?? 64, bottom: savedPageContent.canvasPadding?.bottom ?? 64 });
    } else {
      setDroppedElements([]);
      setHeaderPadding({ top: 32, bottom: 32 });
      setCanvasPadding({ top: 64, bottom: 64 });
    }
    setTimeout(() => setIsLoaded(true), 100);
  }, [pageId, savedPageContent]);

  useEffect(() => {
    if (!isLoaded) return;
    if (isUndoRedo.current) { isUndoRedo.current = false; return; }
    const timer = setTimeout(() => {
      setHistory(prev => {
        const stack = prev.stack.slice(0, prev.index + 1);
        const last = stack[stack.length - 1];
        if (last && JSON.stringify(last) === JSON.stringify(currentState)) return prev;
        const newStack = [...stack, currentState];
        return { stack: newStack, index: newStack.length - 1 };
      });
      if (onPageStateChange) onPageStateChange(pageId, currentState);
    }, 500);
    return () => clearTimeout(timer);
  }, [currentState, isLoaded, pageId, onPageStateChange]);

  const openLibraryWithCallback = useCallback((cb) => { setLibraryCallback(() => cb); setShowLibrary(true); }, []);
  const handleLibrarySelect = useCallback((image) => { if (libraryCallback) { libraryCallback(image); setLibraryCallback(null); } setShowLibrary(false); }, [libraryCallback]);

  const updateElementContent = useCallback((id, newContent, newStyles = null) => {
    setDroppedElements(prev => {
      const updateRecursive = (elements) => elements.map(el => {
        if (el.id === id) {
          let updated = { ...el };
          if (newContent !== undefined && newContent !== null) updated.content = newContent;
          if (newStyles) {
            updated.styles = { ...el.styles, ...newStyles };
            if (el.type === 'button' && newStyles.text !== undefined) updated.content = newStyles.text;
            if (el.type === 'container' && newStyles.anchorId !== undefined) updated.styles.anchorId = newStyles.anchorId;
          }
          return updated;
        }
        if (el.type === 'container' && el.columns) return { ...el, columns: el.columns.map(col => updateRecursive(col)) };
        return el;
      });
      return updateRecursive(prev);
    });
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const state = getState();
      if (onPageStateChange) onPageStateChange(pageId, state);
      await new Promise(r => setTimeout(r, 600));
      setSaveStatus('success');
      if (onSaveComplete) onSaveComplete();
    } catch (e) {
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleSocialLinksUpdate = (links) => { setCurrentSocialLinks(links); if (onSocialLinksChange) onSocialLinksChange(links); };

  const findElementById = useCallback((elements, id) => {
    for (const el of elements) {
      if (el.id === id) return el;
      if (el.type === 'container' && el.columns) { for (const col of el.columns) { const found = findElementById(col, id); if (found) return found; } }
    }
    return null;
  }, []);

  const updateNestedStyles = useCallback((elements, id, newStyles) => {
    return elements.map(el => {
      if (el.id === id) return { ...el, styles: { ...el.styles, ...newStyles } };
      if (el.type === 'container' && el.columns) return { ...el, columns: el.columns.map(col => updateNestedStyles(col, id, newStyles)) };
      return el;
    });
  }, []);

  const handleReorder = useCallback((widgetId, targetContainerId, targetColIdx, targetIndex) => {
    setDroppedElements(prev => moveElement(prev, widgetId, targetContainerId, targetColIdx, targetIndex));
  }, []);

  useEffect(() => {
    if (!marginDrag && !paddingDrag && !headerDrag && !canvasDrag && !widgetResizeDrag) return;

    const handleMouseMove = (e) => {
      if (widgetResizeDrag) {
        if (widgetResizeDrag.type === 'fontSizeTop' || widgetResizeDrag.type === 'fontSizeBottom') {
          const delta = widgetResizeDrag.type === 'fontSizeBottom' ? e.clientY - widgetResizeDrag.startY : widgetResizeDrag.startY - e.clientY;
          const newVal = Math.max(12, Math.min(200, widgetResizeDrag.startVal + Math.round(delta / 2)));
          setLiveResizeVal(newVal);
          setDroppedElements(prev => updateNestedStyles(prev, widgetResizeDrag.id, { fontSize: newVal }));
        } else if (widgetResizeDrag.type === 'paddingXLeft' || widgetResizeDrag.type === 'paddingXRight') {
          const delta = widgetResizeDrag.type === 'paddingXRight' ? widgetResizeDrag.startX - e.clientX : e.clientX - widgetResizeDrag.startX;
          const newVal = Math.max(0, widgetResizeDrag.startVal + delta);
          setLiveResizeVal(Math.round(newVal));
          setDroppedElements(prev => updateNestedStyles(prev, widgetResizeDrag.id, { paddingX: newVal }));
        } else if (widgetResizeDrag.type === 'imageWidth') {
          const delta = e.clientX - widgetResizeDrag.startX;
          const newVal = Math.max(10, Math.min(100, widgetResizeDrag.startVal + Math.round(delta / 5)));
          setLiveResizeVal(newVal);
          setDroppedElements(prev => updateNestedStyles(prev, widgetResizeDrag.id, { width: newVal }));
        } else if (widgetResizeDrag.type === 'imageHeight') {
          const delta = e.clientY - widgetResizeDrag.startY;
          const newVal = Math.max(50, widgetResizeDrag.startVal + delta);
          setLiveResizeVal(Math.round(newVal));
          setDroppedElements(prev => updateNestedStyles(prev, widgetResizeDrag.id, { height: newVal }));
        }
      } else if (marginDrag) {
        const delta = e.clientY - marginDrag.startY;
        let newVal = marginDrag.type === 'marginTop' ? Math.max(0, marginDrag.startVal - delta) : Math.max(0, marginDrag.startVal + delta);
        if (marginDrag.type === 'height') newVal = Math.max(10, marginDrag.startVal + delta);
        setDroppedElements(prev => updateNestedStyles(prev, marginDrag.id, { [marginDrag.type]: newVal }));
      } else if (paddingDrag) {
        const delta = e.clientY - paddingDrag.startY;
        const newVal = paddingDrag.type === 'paddingTop' ? Math.max(0, paddingDrag.startVal - delta) : Math.max(0, paddingDrag.startVal + delta);
        setDroppedElements(prev => updateNestedStyles(prev, paddingDrag.id, { [paddingDrag.type]: newVal }));
      } else if (headerDrag) {
        const delta = e.clientY - headerDrag.startY;
        if (headerDrag.type === 'top') setHeaderPadding(prev => ({ ...prev, top: Math.max(0, headerDrag.startVal - delta) }));
        else setHeaderPadding(prev => ({ ...prev, bottom: Math.max(0, headerDrag.startVal + delta) }));
      } else if (canvasDrag) {
        const delta = e.clientY - canvasDrag.startY;
        if (canvasDrag.type === 'top') setCanvasPadding(prev => ({ ...prev, top: Math.max(0, canvasDrag.startVal - delta) }));
        else setCanvasPadding(prev => ({ ...prev, bottom: Math.max(0, canvasDrag.startVal + delta) }));
      }
    };

    const handleMouseUp = () => {
      setMarginDrag(null); setPaddingDrag(null); setHeaderDrag(null); setCanvasDrag(null); setWidgetResizeDrag(null); setLiveResizeVal(0);
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('mouseup', handleMouseUp);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [marginDrag, paddingDrag, headerDrag, canvasDrag, widgetResizeDrag, updateNestedStyles]);

  const handleContextMenu = (e, elId, type) => { e.preventDefault(); e.stopPropagation(); setContextMenu({ x: e.clientX, y: e.clientY, elId, type }); };

  const handleDrop = (e, targetColumnId = null, targetContainerId = null) => {
    e.preventDefault(); e.stopPropagation();
    setIsDraggingOver(false); setDraggingOverCol(null);
    const reorderId = e.dataTransfer.getData('reorderId');
    if (reorderId) return;
    const type = e.dataTransfer.getData('widgetType');
    if (!type) return;
    if (type === 'container' && targetContainerId !== null) return;
    
    const newElement = buildNewElement(type);
    
    if (type === 'image') {
      setPendingImageWidgetId(newElement.id);
      if (targetContainerId !== null && targetColumnId !== null) {
        setDroppedElements(prev => prev.map(el => el.id === targetContainerId ? { ...el, columns: el.columns.map((col, idx) => idx === targetColumnId ? [...col, newElement] : col) } : el));
      } else { setDroppedElements(prev => [...prev, newElement]); }
      setShowImagePopup(true); return;
    }
    
    if (targetContainerId !== null && targetColumnId !== null) {
      setDroppedElements(prev => prev.map(el => el.id === targetContainerId ? { ...el, columns: el.columns.map((col, idx) => idx === targetColumnId ? [...col, newElement] : col) } : el));
    } else { setDroppedElements(prev => [...prev, newElement]); }
    
    if (type === 'products') { setEditingProductsId(newElement.id); setShowProductsPopup(true); }
    else if (type === 'contact-form') { setEditingContactFormId(newElement.id); setShowContactFormPopup(true); }
    else if (type === 'reviews') { setEditingReviewsId(newElement.id); setShowReviewsPopup(true); }
    else if (type === 'artist') { setEditingArtistId(newElement.id); setShowArtistPopup(true); }
  };

  const handleImageSelect = (imageData) => {
    if (pendingImageWidgetId) {
      setDroppedElements(prev => prev.map(el => {
        if (el.id === pendingImageWidgetId) return { ...el, styles: { ...el.styles, imageUrl: imageData.url, altText: imageData.name || 'Image' } };
        if (el.type === 'container' && el.columns) return { ...el, columns: el.columns.map(col => col.map(w => w.id === pendingImageWidgetId ? { ...w, styles: { ...w.styles, imageUrl: imageData.url, altText: imageData.name || 'Image' } } : w)) };
        return el;
      }));
      setPendingImageWidgetId(null);
    }
  };

  const handleImageWidgetClick = (el) => { setPendingImageWidgetId(el.id); setShowImagePopup(true); };
  const handleImageSettingsClick = (el) => { setEditingImageId(el.id); setShowImageSettingsPopup(true); };

  const splitContainer = (id, count) => {
    setDroppedElements(prev => prev.map(el => {
      if (el.id === id) { const existing = el.columns.flat(); return { ...el, columns: Array.from({ length: count }, (_, i) => i === 0 ? existing : []) }; }
      return el;
    }));
  };

  const removeElement = (id) => {
    setDroppedElements(prev => prev.filter(el => el.id !== id).map(el => {
      if (el.type === 'container') return { ...el, columns: el.columns.map(col => col.filter(w => w.id !== id)) };
      return el;
    }));
  };

  const handleDeleteContainer = (id) => { removeElement(id); setShowContainerPopup(false); setEditingContainerId(null); };
  const availableAnchors = useMemo(() => droppedElements.filter(el => el.type === 'container' && el.styles?.anchorId).map(el => el.styles.anchorId), [droppedElements]);

  const renderLeafWidget = (el, containerId = null, colIdx = null, index = 0, totalInCol = 1) => {
    let innerContent = null;
    
    const startResizeX = (e, type, px) => { e.stopPropagation(); setLiveResizeVal(px); setWidgetResizeDrag({ id: el.id, type, startX: e.clientX, startVal: px }); };
    const startResizeY = (e, type, fs) => { e.stopPropagation(); setLiveResizeVal(fs); setWidgetResizeDrag({ id: el.id, type, startY: e.clientY, startVal: fs }); };
    const startResizeWidth = (e, w) => { e.stopPropagation(); setLiveResizeVal(w); setWidgetResizeDrag({ id: el.id, type: 'imageWidth', startX: e.clientX, startVal: w }); };
    const startResizeHeight = (e, h) => { e.stopPropagation(); setLiveResizeVal(h); setWidgetResizeDrag({ id: el.id, type: 'imageHeight', startY: e.clientY, startVal: h }); };

    if (el.type === 'heading') {
      const px = el.styles?.paddingX || 0; const fs = el.styles?.fontSize || 48;
      innerContent = (
        <div className="py-4 w-full relative group/heading" style={{ paddingLeft: `${px}px`, paddingRight: `${px}px` }}>
          <TextResizeHandles el={el} px={px} fs={fs} groupName="heading" widgetResizeDrag={widgetResizeDrag} startResizeX={(e, type) => startResizeX(e, type, px)} startResizeY={(e, type) => startResizeY(e, type, fs)} />
          <EditableText tag="h2" initialText={el.content} initialStyles={el.styles} onStyleChange={(s) => updateElementContent(el.id, null, s)} onChange={(v) => updateElementContent(el.id, v)} className="font-black tracking-tight w-full" />
        </div>
      );
    } else if (el.type === 'text') {
      const px = el.styles?.paddingX || 0; const fs = el.styles?.fontSize || 24;
      innerContent = (
        <div className="py-2 w-full relative group/text" style={{ paddingLeft: `${px}px`, paddingRight: `${px}px` }}>
          <TextResizeHandles el={el} px={px} fs={fs} groupName="text" widgetResizeDrag={widgetResizeDrag} startResizeX={(e, type) => startResizeX(e, type, px)} startResizeY={(e, type) => startResizeY(e, type, fs)} />
          <EditableText tag="p" initialText={el.content} initialStyles={el.styles} onStyleChange={(s) => updateElementContent(el.id, null, s)} onChange={(v) => updateElementContent(el.id, v)} className="w-full" />
        </div>
      );
    } else if (el.type === 'image') {
      const imageUrl = el.styles?.imageUrl; const width = el.styles?.width || 100; const maxWidth = el.styles?.maxWidth || '100%'; const objectFit = el.styles?.objectFit || 'cover'; const fullScreen = el.styles?.fullScreen || false;
      const h = el.styles?.height === 'auto' ? 300 : (typeof el.styles?.height === 'number' ? el.styles.height : 300);
      innerContent = (
        <div className="py-4 px-4 w-full flex justify-center" style={{ backgroundColor: 'transparent' }}>
          {imageUrl ? (
            <div className="relative group/image overflow-hidden rounded-lg" style={{ backgroundColor: 'transparent', width: `${width}%`, maxWidth: maxWidth, height: fullScreen ? '100vh' : el.styles?.height === 'auto' ? 'auto' : `${el.styles?.height}px` }}>
              <img src={imageUrl} alt={el.styles?.altText || 'Image'} className="w-full h-full" style={{ backgroundColor: 'transparent', objectFit: objectFit }} />
              <ImageResizeHandles el={el} w={width} h={h} widgetResizeDrag={widgetResizeDrag} startResizeWidth={(e) => startResizeWidth(e, width)} startResizeHeight={(e) => startResizeHeight(e, h)} />
              <div className="absolute inset-0 bg-black/0 group-hover/image:bg-black/40 backdrop-blur-[2px] transition-all flex items-center justify-center gap-3 opacity-0 group-hover/image:opacity-100">
                <button onClick={(e) => { e.stopPropagation(); handleImageWidgetClick(el); }} className="bg-white text-blue-600 px-4 py-2 rounded-xl font-bold text-sm shadow-xl hover:scale-105 transition-all flex items-center space-x-2"><SafeIcon icon={FiUploadCloud} className="w-4 h-4" /><span>Change</span></button>
                <button onClick={(e) => { e.stopPropagation(); handleImageSettingsClick(el); }} className="bg-blue-500 text-white px-4 py-2 rounded-xl font-bold text-sm shadow-xl hover:scale-105 transition-all flex items-center space-x-2"><SafeIcon icon={FiSettings} className="w-4 h-4" /><span>Settings</span></button>
                <button onClick={(e) => { e.stopPropagation(); removeElement(el.id); }} className="bg-red-500 text-white px-4 py-2 rounded-xl font-bold text-sm shadow-xl hover:scale-105 transition-all flex items-center space-x-2"><SafeIcon icon={FiTrash2} className="w-4 h-4" /><span>Delete</span></button>
              </div>
            </div>
          ) : (
            <div onClick={(e) => { e.stopPropagation(); handleImageWidgetClick(el); }} className="w-full aspect-video bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-teal-400 hover:bg-teal-50/50 transition-all" style={{ backgroundColor: 'transparent' }}>
              <SafeIcon icon={FiImage} className="w-12 h-12 text-gray-300 mb-2" />
              <span className="text-sm font-bold text-gray-400">Click to add image</span>
            </div>
          )}
        </div>
      );
    } else if (el.type === 'button') {
      const s = el.styles || {};
      innerContent = (
        <div className="py-4 px-4 w-full flex justify-center">
          <button onClick={(e) => { e.stopPropagation(); setEditingButtonId(el.id); setShowButtonPopup(true); }} className="px-10 py-4 font-black text-lg transition-all hover:scale-105" style={{ backgroundColor: s.bgColor, color: s.textColor, borderRadius: `${s.borderRadius || 12}px` }}>
            {s.text || 'Get Started'}
          </button>
        </div>
      );
    } else if (el.type === 'spacer') {
      innerContent = (
        <div className="w-full relative group/spacer block">
          <div style={{ height: `${el.styles?.height || 50}px` }} className="w-full bg-teal-500/5 border border-dashed border-teal-500/20 rounded-lg flex items-center justify-center opacity-0 group-hover/spacer:opacity-100 transition-opacity">
            <span className="text-[10px] font-bold text-teal-600 uppercase tracking-widest">Spacer ({el.styles?.height || 50}px)</span>
          </div>
          <div onMouseDown={(e) => { e.stopPropagation(); setMarginDrag({ id: el.id, type: 'height', startY: e.clientY, startVal: el.styles?.height || 50 }); }} className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 cursor-ns-resize opacity-0 group-hover/spacer:opacity-100 bg-teal-500 text-white text-[10px] px-2 py-0.5 rounded shadow-md z-50 flex items-center space-x-1">
            <SafeIcon icon={FiMaximize} className="w-3 h-3 rotate-45" /><span>Resize</span>
          </div>
        </div>
      );
    } else if (el.type === 'products') {
      innerContent = (
        <div className="py-4 px-4 w-full">
          <ProductsWidget styles={el.styles} projectId={activeWebsiteId} onConfigure={() => { setEditingProductsId(el.id); setShowProductsPopup(true); }} />
          <div className="flex justify-center mt-2 opacity-0 group-hover/widget:opacity-100 transition-opacity">
            <button onClick={(e) => { e.stopPropagation(); setEditingProductsId(el.id); setShowProductsPopup(true); }} className="bg-teal-500 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full flex items-center space-x-1.5 hover:bg-teal-600 transition-colors shadow">
              <SafeIcon icon={FiSettings} className="w-3 h-3" /><span>Configure Products</span>
            </button>
          </div>
        </div>
      );
    } else if (el.type === 'contact-form') {
      innerContent = (
        <div className="py-4 px-4 w-full">
          <ContactFormWidget styles={el.styles} onSubmitPreview={() => { setEditingContactFormId(el.id); setShowContactFormPopup(true); }} />
          <div className="flex justify-center mt-2 opacity-0 group-hover/widget:opacity-100 transition-opacity">
            <button onClick={(e) => { e.stopPropagation(); setEditingContactFormId(el.id); setShowContactFormPopup(true); }} className="bg-teal-500 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full flex items-center space-x-1.5 hover:bg-teal-600 transition-colors shadow">
              <SafeIcon icon={FiSettings} className="w-3 h-3" /><span>Configure Form</span>
            </button>
          </div>
        </div>
      );
    } else if (el.type === 'reviews') {
      innerContent = (
        <div className="py-4 px-4 w-full">
          <ReviewsWidget styles={el.styles} />
          <div className="flex justify-center mt-2 opacity-0 group-hover/widget:opacity-100 transition-opacity">
            <button onClick={(e) => { e.stopPropagation(); setEditingReviewsId(el.id); setShowReviewsPopup(true); }} className="bg-yellow-500 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full flex items-center space-x-1.5 hover:bg-yellow-600 transition-colors shadow">
              <SafeIcon icon={FiSettings} className="w-3 h-3" /><span>Configure Reviews</span>
            </button>
          </div>
        </div>
      );
    } else if (el.type === 'artist') {
      innerContent = (
        <div className="py-4 px-4 w-full">
          <ArtistWidget styles={el.styles} onConfigure={() => { setEditingArtistId(el.id); setShowArtistPopup(true); }} />
          <div className="flex justify-center mt-2 opacity-0 group-hover/widget:opacity-100 transition-opacity">
            <button onClick={(e) => { e.stopPropagation(); setEditingArtistId(el.id); setShowArtistPopup(true); }} className="bg-purple-500 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full flex items-center space-x-1.5 hover:bg-purple-600 transition-colors shadow">
              <SafeIcon icon={FiSettings} className="w-3 h-3" /><span>Configure Music</span>
            </button>
          </div>
        </div>
      );
    }

    if (!innerContent) return null;

    return (
      <React.Fragment key={el.id}>
        <DropZone containerId={containerId} colIdx={colIdx} index={index} onReorder={handleReorder} />
        <WidgetWrapper el={el} onContextMenu={(e) => handleContextMenu(e, el.id, el.type)} onDelete={() => removeElement(el.id)}>
          {innerContent}
        </WidgetWrapper>
        {index === totalInCol - 1 && (<DropZone containerId={containerId} colIdx={colIdx} index={totalInCol} onReorder={handleReorder} />)}
      </React.Fragment>
    );
  };

  const renderContainerWidget = (el) => {
    const { backgroundType, backgroundColor, backgroundImage, overlayOpacity = 0, overlayColor = '#000000', anchorId } = el.styles || {};
    const pTop = el.styles?.paddingTop !== undefined ? el.styles.paddingTop : (el.styles?.padding || 48);
    const pBottom = el.styles?.paddingBottom !== undefined ? el.styles.paddingBottom : (el.styles?.padding || 48);
    const bgStyle = backgroundType === 'image' && backgroundImage ? { backgroundImage: `url(${backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : { backgroundColor: backgroundColor || '#ffffff' };
    const isDraggingThisPad = paddingDrag?.id === el.id;
    const isDraggingPadTop = isDraggingThisPad && paddingDrag?.type === 'paddingTop';
    const isDraggingPadBottom = isDraggingThisPad && paddingDrag?.type === 'paddingBottom';

    return (
      <div id={anchorId || undefined} onContextMenu={(e) => handleContextMenu(e, el.id, 'container')} style={{ ...bgStyle, paddingTop: `${pTop}px`, paddingBottom: `${pBottom}px` }} className="w-full border-y border-dashed border-gray-100 hover:border-teal-200 group/container relative transition-colors">
        {backgroundType === 'image' && backgroundImage && overlayOpacity > 0 && (
          <div className="absolute inset-0 pointer-events-none z-[1]" style={{ backgroundColor: overlayColor, opacity: overlayOpacity }} />
        )}
        <div onMouseDown={(e) => { e.stopPropagation(); setPaddingDrag({ id: el.id, type: 'paddingTop', startY: e.clientY, startVal: pTop }); }} className={`absolute top-0 left-0 w-full h-8 cursor-ns-resize flex items-start justify-center z-[50] transition-opacity duration-200 ${isDraggingPadTop ? 'opacity-100' : 'opacity-0 group-hover/container:opacity-100'}`}>
          {isDraggingPadTop && <div className="absolute top-0 left-0 w-full bg-blue-500/10 border-b border-blue-500/30" style={{ height: `${pTop}px` }} />}
          <div className="bg-blue-600 text-white text-[10px] font-bold px-3 py-1 rounded-b-md shadow-md">Stretch Top: {pTop}px</div>
        </div>
        <div className={`flex w-full gap-4 px-6 relative z-[2] ${deviceView !== 'desktop' ? 'flex-col' : ''}`}>
          {el.columns.map((col, colIdx) => {
            const isOver = draggingOverCol?.containerId === el.id && draggingOverCol?.colIdx === colIdx;
            return (
              <div key={colIdx} onDragOver={(e) => { if (e.dataTransfer.types.includes('reorderid')) return; e.preventDefault(); e.stopPropagation(); setDraggingOverCol({ containerId: el.id, colIdx }); }} onDragLeave={(e) => { e.stopPropagation(); setDraggingOverCol(null); }} onDrop={(e) => handleDrop(e, colIdx, el.id)} className={`flex-1 min-h-[100px] rounded-2xl border-2 border-dashed flex flex-col items-stretch relative transition-all duration-200 ${isOver ? 'border-teal-400 bg-teal-50/40 scale-[1.01]' : 'border-gray-200/60 hover:border-teal-200'}`}>
                {col.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className={`text-[10px] uppercase font-black tracking-[0.2em] text-center px-4 transition-colors ${isOver ? 'text-teal-500' : 'text-gray-300'}`}>{isOver ? '+ Drop Here' : 'Drop Widget'}</div>
                  </div>
                ) : (
                  <div className="flex flex-col w-full py-2">{col.map((widget, wIdx) => renderLeafWidget(widget, el.id, colIdx, wIdx, col.length))}</div>
                )}
              </div>
            );
          })}
        </div>
        <div className="absolute top-2 right-2 z-[80] opacity-0 group-hover/container:opacity-100 transition-opacity flex items-center space-x-2 pointer-events-auto">
          <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingContainerId(el.id); setShowContainerPopup(true); }} className="bg-teal-500 text-white px-3 py-1.5 rounded-full border border-teal-600 shadow-lg flex items-center space-x-1.5 hover:bg-teal-600 transition-all font-black text-[10px] uppercase tracking-widest">
            <SafeIcon icon={FiSettings} className="w-3 h-3" /><span>Section</span>
          </button>
          <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeElement(el.id); }} className="bg-red-500 text-white px-3 py-1.5 rounded-full border border-red-600 shadow-lg flex items-center space-x-1.5 hover:bg-red-600 transition-all font-black text-[10px] uppercase tracking-widest">
            <SafeIcon icon={FiTrash2} className="w-3 h-3" /><span>Delete</span>
          </button>
        </div>
        <div onMouseDown={(e) => { e.stopPropagation(); setPaddingDrag({ id: el.id, type: 'paddingBottom', startY: e.clientY, startVal: pBottom }); }} className={`absolute bottom-0 left-0 w-full h-8 cursor-ns-resize flex items-end justify-center z-[50] transition-opacity duration-200 ${isDraggingPadBottom ? 'opacity-100' : 'opacity-0 group-hover/container:opacity-100'}`}>
          {isDraggingPadBottom && <div className="absolute bottom-0 left-0 w-full bg-blue-500/10 border-t border-blue-500/30" style={{ height: `${pBottom}px` }} />}
          <div className="bg-blue-600 text-white text-[10px] font-bold px-3 py-1 rounded-t-md shadow-md">Stretch Bottom: {pBottom}px</div>
        </div>
      </div>
    );
  };

  const renderWidget = (el, index, total) => {
    const mTop = el.styles?.marginTop || 0;
    const mBottom = el.styles?.marginBottom || 0;
    const isDraggingThis = marginDrag?.id === el.id;
    const isDraggingTop = isDraggingThis && marginDrag?.type === 'marginTop';
    const isDraggingBottom = isDraggingThis && marginDrag?.type === 'marginBottom';
    const content = el.type === 'container' ? <div className="relative group/container-wrapper w-full">{renderContainerWidget(el)}</div> : renderLeafWidget(el, null, null, index, total);

    return (
      <React.Fragment key={el.id}>
        {el.type === 'container' && (<DropZone containerId={null} colIdx={null} index={index} onReorder={handleReorder} />)}
        <div className="relative group/spacing w-full flex flex-col items-center" style={{ paddingTop: `${mTop}px`, paddingBottom: `${mBottom}px` }}>
          {mTop > 0 && isDraggingTop && <div className="absolute top-0 w-full bg-teal-400/10 border-b border-teal-400/30" style={{ height: `${mTop}px` }} />}
          <div onMouseDown={(e) => { e.stopPropagation(); setMarginDrag({ id: el.id, type: 'marginTop', startY: e.clientY, startVal: mTop }); }} className={`absolute top-0 left-0 w-full h-4 -translate-y-1/2 cursor-ns-resize flex items-center justify-center z-[60] transition-opacity duration-200 ${isDraggingTop ? 'opacity-100' : 'opacity-0 group-hover/spacing:opacity-100'}`}>
            <div className="w-full h-[2px] bg-teal-400 border-y border-white"></div>
            <div className="absolute bg-teal-500 text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-sm border border-teal-600">{mTop}px</div>
          </div>
          {content}
          {mBottom > 0 && isDraggingBottom && <div className="absolute bottom-0 w-full bg-teal-400/10 border-t border-teal-400/30" style={{ height: `${mBottom}px` }} />}
          <div onMouseDown={(e) => { e.stopPropagation(); setMarginDrag({ id: el.id, type: 'marginBottom', startY: e.clientY, startVal: mBottom }); }} className={`absolute bottom-0 left-0 w-full h-4 translate-y-1/2 cursor-ns-resize flex items-center justify-center z-[60] transition-opacity duration-200 ${isDraggingBottom ? 'opacity-100' : 'opacity-0 group-hover/spacing:opacity-100'}`}>
            <div className="w-full h-[2px] bg-teal-400 border-y border-white"></div>
            <div className="absolute bg-teal-500 text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-sm border border-teal-600">{mBottom}px</div>
          </div>
        </div>
        {el.type === 'container' && index === total - 1 && (<DropZone containerId={null} colIdx={null} index={total} onReorder={handleReorder} />)}
      </React.Fragment>
    );
  };

  const currentContainerStyles = editingContainerId ? (findElementById(droppedElements, editingContainerId)?.styles || {}) : {};
  const currentButtonStyles = editingButtonId ? (() => { const s = findElementById(droppedElements, editingButtonId)?.styles || {}; return { ...s, linkType: s.linkType || (s.link?.startsWith('#') ? 'anchor' : 'url') }; })() : {};
  const currentProductsStyles = editingProductsId ? (findElementById(droppedElements, editingProductsId)?.styles || {}) : {};
  const currentContactFormStyles = editingContactFormId ? (findElementById(droppedElements, editingContactFormId)?.styles || {}) : {};
  const currentReviewsStyles = editingReviewsId ? (findElementById(droppedElements, editingReviewsId)?.styles || {}) : {};
  const currentArtistStyles = editingArtistId ? (findElementById(droppedElements, editingArtistId)?.styles || {}) : {};
  const currentImageStyles = editingImageId ? (findElementById(droppedElements, editingImageId)?.styles || {}) : {};
  const isAnyDragging = marginDrag || paddingDrag || headerDrag || canvasDrag || widgetResizeDrag;
  const headerBgColor = menuStyles.backgroundColor || '#ffffff';
  const menuTextColor = menuStyles.textColor || '#4b5563';
  const menuHoverColor = menuStyles.hoverColor || '#2dd4bf';

  const getContainerWidthClass = () => {
    switch (deviceView) { case 'tablet': return 'max-w-[768px]'; case 'mobile': return 'max-w-[375px]'; default: return 'max-w-[1240px]'; }
  };

  return (
    <div className={`flex-1 bg-[#f0f2f5] overflow-y-auto relative flex flex-col items-center py-12 px-6 custom-scrollbar ${isAnyDragging ? 'select-none' : ''}`}>

      <AnimatePresence>
        {widgetResizeDrag && (
          <motion.div key="resize-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
            <ResizeDragOverlay drag={widgetResizeDrag} liveVal={liveResizeVal} />
          </motion.div>
        )}
      </AnimatePresence>

      {activePage && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[80] pointer-events-none">
          <div className="bg-white/80 backdrop-blur-sm border border-gray-200 text-gray-500 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full shadow-sm flex items-center space-x-2">
            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full" /><span>{activePage.name}</span>
            <span className="text-gray-300 font-mono">{activePage.slug}</span>
          </div>
        </div>
      )}

      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-[#0a0a0a]/95 backdrop-blur-md rounded-full shadow-2xl border border-[#2c313a] px-5 py-2 flex items-center space-x-3 z-[90] pointer-events-auto">
        <div className="flex items-center space-x-1 border-r border-[#2c313a] pr-4">
          {[['desktop', FiMonitor], ['tablet', FiTablet], ['mobile', FiSmartphone]].map(([mode, Icon]) => (
            <button key={mode} onClick={() => setDeviceView(mode)} className={`p-2 rounded-lg transition-colors ${deviceView === mode ? 'text-[#c8f542] bg-[#c8f542]/10' : 'hover:text-gray-200 text-gray-500'}`}>
              <SafeIcon icon={Icon} className="w-4 h-4" />
            </button>
          ))}
        </div>
        <button onClick={onOpenPreview} className="flex items-center space-x-2 px-4 py-2 bg-[#1e2228] hover:bg-[#2c313a] border border-[#2c313a] text-gray-300 hover:text-white rounded-full text-xs font-black uppercase tracking-widest transition-all">
          <SafeIcon icon={FiEye} className="w-3.5 h-3.5" /><span>Test Preview</span>
        </button>
        <AnimatePresence>{saveStatus && <SaveBadge status={saveStatus} />}</AnimatePresence>
        <button onClick={handleSave} disabled={isSaving} className="flex items-center space-x-2 px-5 py-2 bg-[#c8f542] hover:bg-[#d4ff50] text-black rounded-full text-xs font-black uppercase tracking-widest transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-[#c8f542]/20">
          <SafeIcon icon={isSaving ? FiCloud : FiSave} className={`w-3.5 h-3.5 ${isSaving ? 'animate-pulse' : ''}`} />
          <span>{isSaving ? 'Saving...' : 'Save Layout'}</span>
        </button>
      </div>

      <div className={`w-full ${getContainerWidthClass()} bg-white shadow-2xl rounded-xl relative flex flex-col border border-gray-100 mb-32 transition-all duration-300 mx-auto`}>
        <header className="px-10 flex justify-between items-center border-b border-gray-50 group/header relative transition-colors z-30" style={{ backgroundColor: headerBgColor, paddingTop: `${headerPadding?.top ?? 32}px`, paddingBottom: `${headerPadding?.bottom ?? 32}px` }}>
          <div onMouseDown={(e) => { e.stopPropagation(); setHeaderDrag({ type: 'top', startY: e.clientY, startVal: headerPadding?.top ?? 32 }); }} className={`absolute top-0 left-0 w-full h-4 -translate-y-1/2 cursor-ns-resize flex items-center justify-center z-[60] transition-opacity duration-200 ${headerDrag?.type === 'top' ? 'opacity-100' : 'opacity-0 group-hover/header:opacity-100'}`}>
            <div className="w-full h-[2px] bg-indigo-400 border-y border-white"></div>
            <div className="absolute bg-indigo-500 text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-sm border border-indigo-600">Header Top: {headerPadding?.top ?? 32}px</div>
          </div>

          <div onClick={() => setShowLogoPopup(true)} className="cursor-pointer hover:ring-2 hover:ring-teal-400 p-2 rounded-xl transition-all">
            {logoData.type === 'text' ? (<div className="text-2xl font-black text-gray-900 tracking-tighter">{logoData.content}</div>) : (<img src={logoData.url} className="h-10 w-auto" alt="logo" />)}
          </div>

          <div className="flex items-center space-x-6">
            {deviceView === 'desktop' && (
              <div className="flex items-center border-r border-gray-200 pr-6 mr-2 space-x-1">
                {menuItems.map(item => (
                  <a key={item.id} href={item.link || '#'} onClick={(e) => handleNavClick(e, item.link, item.linkType)} className="text-sm font-bold transition-colors px-3 py-1 rounded-lg hover:bg-gray-100 cursor-pointer" style={{ color: menuTextColor }} onMouseEnter={e => e.currentTarget.style.color = menuHoverColor} onMouseLeave={e => e.currentTarget.style.color = menuTextColor}>
                    {item.label}
                  </a>
                ))}
                <button onClick={(e) => { e.stopPropagation(); setShowMenuPopup(true); }} title="Edit Menu" className="ml-2 p-1.5 bg-teal-50 hover:bg-teal-100 text-teal-600 rounded-lg transition-colors border border-teal-200">
                  <SafeIcon icon={FiSettings} className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <div className="flex items-center space-x-5">
              {deviceView === 'desktop' && (
                <span className="text-sm font-bold cursor-pointer transition-colors" style={{ color: menuTextColor }} onMouseEnter={e => e.target.style.color = menuHoverColor} onMouseLeave={e => e.target.style.color = menuTextColor}>Login</span>
              )}
              <div className="relative cursor-pointer transition-colors" style={{ color: menuTextColor }} onMouseEnter={e => e.currentTarget.style.color = menuHoverColor} onMouseLeave={e => e.currentTarget.style.color = menuHoverColor} onClick={() => setIsCartOpen(true)}>
                <SafeIcon icon={FiShoppingCart} className="w-5 h-5" />
                <span className="absolute -top-1.5 -right-1.5 bg-teal-500 text-white text-[9px] font-bold w-3.5 h-3.5 rounded-full flex items-center justify-center">{cartCount}</span>
              </div>
              {deviceView !== 'desktop' && (
                <div className="relative ml-2">
                  <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 rounded-lg hover:bg-black/5 transition-colors">
                    <SafeIcon icon={FiMenu} className="w-6 h-6" style={{ color: menuTextColor }} />
                  </button>
                  <AnimatePresence>
                    {isMobileMenuOpen && (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="absolute top-full right-0 mt-4 bg-white shadow-2xl rounded-xl border border-gray-100 min-w-[200px] overflow-hidden z-[100]">
                        <div className="py-2">
                          {menuItems.map(item => (
                            <a key={item.id} href={item.link || '#'} onClick={(e) => { handleNavClick(e, item.link, item.linkType); setIsMobileMenuOpen(false); }} className="block px-5 py-3 text-sm font-semibold hover:bg-gray-50 transition-colors" style={{ color: menuTextColor }}>
                              {item.label}
                            </a>
                          ))}
                        </div>
                        <div className="p-2 border-t border-gray-100 bg-gray-50">
                          <button onClick={(e) => { e.stopPropagation(); setShowMenuPopup(true); setIsMobileMenuOpen(false); }} className="w-full py-2.5 text-xs font-black uppercase tracking-widest text-teal-600 bg-teal-50 hover:bg-teal-100 rounded-xl transition-colors text-center">Edit Menu</button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>

          <div onMouseDown={(e) => { e.stopPropagation(); setHeaderDrag({ type: 'bottom', startY: e.clientY, startVal: headerPadding?.bottom ?? 32 }); }} className={`absolute bottom-0 left-0 w-full h-4 translate-y-1/2 cursor-ns-resize flex items-center justify-center z-[60] transition-opacity duration-200 ${headerDrag?.type === 'bottom' ? 'opacity-100' : 'opacity-0 group-hover/header:opacity-100'}`}>
            <div className="w-full h-[2px] bg-indigo-400 border-y border-white"></div>
            <div className="absolute bg-indigo-500 text-white text-[10px] font-bold px-3 py-1 rounded shadow-sm border border-indigo-600">Header Bottom: {headerPadding?.bottom ?? 32}px</div>
          </div>
        </header>

        <div ref={canvasRef} onDragOver={(e) => { if (e.dataTransfer.types.includes('reorderid')) return; e.preventDefault(); setIsDraggingOver(true); }} onDragLeave={() => setIsDraggingOver(false)} onDrop={(e) => handleDrop(e)} style={{ paddingTop: `${canvasPadding?.top ?? 64}px`, paddingBottom: `${canvasPadding?.bottom ?? 64}px` }} className={`flex-1 flex flex-col items-center transition-colors relative group/canvas ${isDraggingOver ? 'bg-teal-50/20 border-4 border-dashed border-teal-200 rounded-3xl mx-4 my-4' : ''}`}>
          <div onMouseDown={(e) => { e.stopPropagation(); setCanvasDrag({ type: 'top', startY: e.clientY, startVal: canvasPadding?.top ?? 64 }); }} className={`absolute top-0 left-0 w-full h-4 -translate-y-1/2 cursor-ns-resize flex items-center justify-center z-[50] transition-opacity duration-200 ${canvasDrag?.type === 'top' ? 'opacity-100' : 'opacity-0 group-hover/canvas:opacity-100'}`}>
            <div className="w-full h-[2px] bg-purple-400 border-y border-white"></div>
            <div className="absolute bg-purple-500 text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-sm border border-purple-600">Page Top: {canvasPadding?.top ?? 64}px</div>
          </div>

          {droppedElements.length === 0 ? (
            <div className="flex flex-col items-center space-y-6 text-gray-200 mt-48">
              <div className="w-24 h-24 rounded-full border-4 border-dashed border-gray-100 flex items-center justify-center bg-gray-50/50"><SafeIcon icon={FiPlus} className="w-10 h-10" /></div>
              <p className="font-black text-sm tracking-[0.3em] text-gray-300 uppercase">Start Building Your Page</p>
            </div>
          ) : (
            <div className="w-full">{droppedElements.map((el, idx) => renderWidget(el, idx, droppedElements.length))}</div>
          )}

          <div onMouseDown={(e) => { e.stopPropagation(); setCanvasDrag({ type: 'bottom', startY: e.clientY, startVal: canvasPadding?.bottom ?? 64 }); }} className={`absolute bottom-0 left-0 w-full h-4 translate-y-1/2 cursor-ns-resize flex items-center justify-center z-[50] transition-opacity duration-200 ${canvasDrag?.type === 'bottom' ? 'opacity-100' : 'opacity-0 group-hover/canvas:opacity-100'}`}>
            <div className="w-full h-[2px] bg-purple-400 border-y border-white"></div>
            <div className="absolute bg-purple-500 text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-sm border border-purple-600">Page Bottom: {canvasPadding?.bottom ?? 64}px</div>
          </div>
        </div>

        <GlobalFooter footerData={footerData} onUpdate={handleFooterUpdate} isEditable={true} onNavClick={handleNavClick} />
      </div>

      {whatsappWidget.enabled && whatsappWidget.number && (
        <a 
          href={`https://wa.me/${whatsappWidget.number.replace(/[^0-9]/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-6 right-6 z-[100] w-14 h-14 bg-[#25D366] hover:bg-[#20BA5A] rounded-full flex items-center justify-center shadow-2xl transition-all hover:scale-110"
          title="Chat with us on WhatsApp"
        >
          <SafeIcon icon={FiMessageCircle} className="w-7 h-7 text-white" />
        </a>
      )}

      <AnimatePresence>
        {contextMenu && (<ContextMenu x={contextMenu.x} y={contextMenu.y} type={contextMenu.type} onClose={() => setContextMenu(null)} onDelete={() => removeElement(contextMenu.elId)} onSplit={(count) => splitContainer(contextMenu.elId, count)} />)}
      </AnimatePresence>

      {showLogoPopup && <LogoPopup currentData={logoData} onUpdate={handleLogoUpdate} onClose={() => setShowLogoPopup(false)} onOpenLibrary={openLibraryWithCallback} />}
      {showMenuPopup && <MenuPopup menuItems={menuItems} onUpdate={handleMenuItemsUpdate} onClose={() => setShowMenuPopup(false)} availableAnchors={availableAnchors} pages={pages || []} />}
      {showButtonPopup && <ButtonPopup currentStyles={currentButtonStyles} onUpdate={(s) => setDroppedElements(prev => updateNestedStyles(prev, editingButtonId, s))} onClose={() => setShowButtonPopup(false)} availableAnchors={availableAnchors} />}
      {showContainerPopup && (<ContainerPopup currentStyles={currentContainerStyles} onUpdate={(s) => setDroppedElements(prev => updateNestedStyles(prev, editingContainerId, s))} onClose={() => { setShowContainerPopup(false); setEditingContainerId(null); }} onOpenLibrary={openLibraryWithCallback} onDelete={() => handleDeleteContainer(editingContainerId)} />)}
      {showProductsPopup && (<ProductsWidgetPopup currentStyles={currentProductsStyles} onUpdate={(s) => setDroppedElements(prev => updateNestedStyles(prev, editingProductsId, s))} onClose={() => { setShowProductsPopup(false); setEditingProductsId(null); }} projectId={activeWebsiteId} />)}
      {showContactFormPopup && (<ContactFormPopup currentStyles={currentContactFormStyles} onUpdate={(s) => setDroppedElements(prev => updateNestedStyles(prev, editingContactFormId, s))} onClose={() => { setShowContactFormPopup(false); setEditingContactFormId(null); }} />)}
      {showReviewsPopup && (<ReviewsWidgetPopup currentStyles={currentReviewsStyles} onUpdate={(s) => setDroppedElements(prev => updateNestedStyles(prev, editingReviewsId, s))} onClose={() => { setShowReviewsPopup(false); setEditingReviewsId(null); }} projectId={activeWebsiteId} />)}
      {showArtistPopup && (<ArtistWidgetPopup currentStyles={currentArtistStyles} onUpdate={(s) => setDroppedElements(prev => updateNestedStyles(prev, editingArtistId, s))} onClose={() => { setShowArtistPopup(false); setEditingArtistId(null); }} />)}
      {showImageSettingsPopup && (<ImageSettingsPopup currentStyles={currentImageStyles} onUpdate={(s) => { setDroppedElements(prev => updateNestedStyles(prev, editingImageId, s)); setShowImageSettingsPopup(false); setEditingImageId(null); }} onClose={() => { setShowImageSettingsPopup(false); setEditingImageId(null); }} />)}
      {showLibrary && <LibraryPopup onClose={() => { setShowLibrary(false); setLibraryCallback(null); }} onSelect={handleLibrarySelect} mode="select" />}
      {showImagePopup && (<ImageWidgetPopup onClose={() => { setShowImagePopup(false); setPendingImageWidgetId(null); }} onSelect={handleImageSelect} onOpenLibrary={openLibraryWithCallback} />)}
      {showSocialLinksPopup && <SocialLinksPopup onClose={() => setShowSocialLinksPopup(false)} onUpdate={handleSocialLinksUpdate} currentLinks={currentSocialLinks} />}

      <AnimatePresence>
        {showPreview && (<LivePreviewOverlay elements={droppedElements} headerPadding={headerPadding} canvasPadding={canvasPadding} logoData={logoData} menuItems={menuItems} menuStyles={menuStyles} footerData={footerData} onClose={onClosePreview} onNavigate={onNavigateToPage} />)}
      </AnimatePresence>
    </div>
  );
});

export default Canvas;